// =============================================================================
// src/data/ai-surfaces/crisis-surfaces.ts — Superfícies de IA da Gestão de Crise.
//
// Takeover #crisisTakeover (src/features/crisis/**). Campos ancorados em
//   buildCrisisVolumeSeries/buildCrisisLiveActions (src/data/sentinel-overlay.ts).
// =============================================================================

import type { AiSurfaceEntry } from '../ai-surface-types';

export const CRISIS_AI_SURFACES: readonly AiSurfaceEntry[] = [
  {
    id: 'crise.panorama',
    label: 'Panorama da crise',
    domain: 'crise',
    location: 'Takeover Crise · #crz-panorama (menções/alcance/sentimento)',
    renderRef: 'src/features/crisis/index.ts#renderMarkup',
    aiKind: 'sentiment',
    backendLayer: 'camada-2',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_sentiment_24h' },
    status: 'partial',
    inputs: ['menções 24h', 'sentimento ponderado', 'aceleração vs baseline'],
    fields: [
      { uiField: 'crz.mencoes', backendField: 'total', type: 'number' },
      { uiField: 'crz.sentimento', backendField: 'avg_weighted_score', type: 'number' },
      { uiField: 'crz.negativo', backendField: 'neg_pct', type: 'percent' },
    ],
  },
  {
    id: 'crise.evolucao',
    label: 'Evolução de menções (24h)',
    domain: 'crise',
    location: 'Takeover Crise · #crz-evo-plot',
    renderRef: 'src/data/sentinel-overlay.ts#buildCrisisVolumeSeries',
    aiKind: 'forecast',
    backendLayer: 'camada-2',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_sentiment_24h' },
    status: 'live',
    inputs: ['volume total 24h'],
    fields: [
      { uiField: 'crz.evoSeries', backendField: 'total', type: 'number', transform: 'buildCrisisVolumeSeries (24 pts)' },
    ],
  },
  {
    id: 'crise.sentimento',
    label: 'Quebra de sentimento',
    domain: 'crise',
    location: 'Takeover Crise · barra neg/neu/pos',
    renderRef: 'src/features/crisis/index.ts#renderMarkup',
    aiKind: 'sentiment',
    backendLayer: 'camada-2',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_sentiment_24h' },
    status: 'live',
    inputs: ['classificação NLP por item'],
    fields: [
      { uiField: 'crz.neg', backendField: 'neg_pct', type: 'percent' },
      { uiField: 'crz.neu', backendField: 'neu_pct', type: 'percent' },
      { uiField: 'crz.pos', backendField: 'pos_pct', type: 'percent' },
    ],
  },
  {
    id: 'crise.amplificadores',
    label: 'Amplificadores',
    domain: 'crise',
    location: 'Takeover Crise · ranking de difusores',
    renderRef: 'src/features/crisis/index.ts#renderMarkup',
    aiKind: 'classification',
    backendLayer: 'camada-2',
    backendSource: { kind: 'hasura-table', name: 'monitoring.content_analysis' },
    status: 'mock',
    inputs: ['entidades mencionadas', 'alcance por autor'],
    fields: [
      { uiField: 'crz.amp[].ator', backendField: 'entities_mentioned', type: 'json' },
      { uiField: 'crz.amp[].alcance', backendField: 'estimated_reach', type: 'number' },
    ],
  },
  {
    id: 'crise.mencoes-extrato',
    label: 'Extrato de menções',
    domain: 'crise',
    location: 'Takeover Crise · #crz-disc (amostra)',
    renderRef: 'src/features/crisis/index.ts#renderMarkup',
    aiKind: 'classification',
    backendLayer: 'camada-2',
    backendSource: { kind: 'hasura-view', name: 'monitoring.v_feed_recent' },
    status: 'partial',
    inputs: ['menções representativas', 'sentimento por item'],
    fields: [
      { uiField: 'crz.mencao.texto', backendField: 'body_preview', type: 'string' },
      { uiField: 'crz.mencao.autor', backendField: 'author_name', type: 'string' },
    ],
  },
  {
    id: 'crise.plano-contingencia',
    label: 'Plano de contingência',
    domain: 'crise',
    location: 'Takeover Crise · #crzAiTitle (fases AGORA/1-3h/3-8h)',
    renderRef: 'src/data/sentinel-overlay.ts#buildCrisisLiveActions',
    aiKind: 'recommendation',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-table', name: 'gold.ai_action_recommendation' },
    status: 'live',
    inputs: ['recommended_actions por janela', 'sinais de crise'],
    fields: [
      { uiField: 'crz.acao[].when', backendField: 'recommended_actions[].when', type: 'string' },
      { uiField: 'crz.acao[].text', backendField: 'recommended_actions[].text', type: 'string' },
      { uiField: 'crz.acao[].tags', backendField: 'recommended_actions[].tags', type: 'string[]' },
    ],
  },
  {
    id: 'crise.enquadramento',
    label: 'Enquadramento (boato/fato/meu)',
    domain: 'crise',
    location: 'Takeover Crise · card de enquadramento',
    renderRef: 'src/features/crisis/crisis-narrative-data.ts#CRISIS_NARRATIVE_FRAME',
    aiKind: 'recommendation',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-table', name: 'gold.ai_action_recommendation' },
    status: 'mock',
    inputs: ['desconstrução de desinformação', 'fato verificável'],
    fields: [
      { uiField: 'frame.boato', backendField: 'recommended_actions[].rumor', type: 'string' },
      { uiField: 'frame.fato', backendField: 'recommended_actions[].fact', type: 'string' },
      { uiField: 'frame.meu', backendField: 'recommended_actions[].reframe', type: 'string' },
    ],
  },
  {
    id: 'crise.modos-narrativos',
    label: 'Modos de resposta',
    domain: 'crise',
    location: 'Takeover Crise · seletor ancorar-fato/trocar-eixo/não-alimentar',
    renderRef: 'src/features/crisis/crisis-narrative-data.ts#CRISIS_NARRATIVE_MODES',
    aiKind: 'recommendation',
    backendLayer: 'camada-3',
    backendSource: { kind: 'hasura-table', name: 'gold.ai_action_recommendation' },
    status: 'mock',
    inputs: ['leitura do público', 'modo de resposta recomendado'],
    fields: [
      { uiField: 'modo.recomendado', backendField: 'action_type', type: 'enum' },
    ],
  },
];
